import {useCallback, useMemo, useState} from 'react'

export interface DialogHandle {
    readonly isOpen: boolean
    open: () => void
    close: () => void
    toggle: () => void
    readonly bind: {
        open: boolean
        onOpenChange: (open: boolean) => void
    }
}

/**
 * Minimal dialog state helper: a boolean + `open`/`close`/`toggle` + a
 * ready-to-spread `bind` prop for Radix-style `<Dialog open={...} onOpenChange={...}>`.
 *
 * Replaces the `const [fooOpen, setFooOpen] = useState(false)` boilerplate
 * that repeats dozens of times across pages.
 */
export function useDialog(initialOpen: boolean = false): DialogHandle {
    const [isOpen, setIsOpen] = useState(initialOpen)

    const open = useCallback(() => setIsOpen(true), [])
    const close = useCallback(() => setIsOpen(false), [])
    const toggle = useCallback(() => setIsOpen(prev => !prev), [])

    const bind = useMemo(
        () => ({open: isOpen, onOpenChange: setIsOpen}),
        [isOpen],
    )

    return {isOpen, open, close, toggle, bind}
}
